/**
 * @architecture-header 1
 * @layer module
 * @domain support
 * @responsibility Render the SH-F07 support-case modal opened from HelpPopover's 聯絡客服 element:
 *   new-case form, the reporter's own case list, one case's message thread with a reply box, and
 *   the Organization's case list as a second tab -- visible only once a real read of it succeeds
 *   (round-1 finding 1: gating, not click-then-403), and reply eligibility gated on the viewer
 *   actually being the case's own reporter, not status alone.
 * @state-owner TanStack Query plus modal-local view/form state
 * @allowed-imports React; TanStack Query; app environment/role/session provider; shared
 *   overlay/error primitives; same-module hooks
 * @forbidden routes; direct API client; a client-side role/membership guess that never checks the
 *   real backend -- the Organization tab's own visibility is real backend authorization surfaced
 *   as a query's success/error state, not a separately-invented permission model
 * @public-api SupportModal
 * @side-effects creates a support case, appends a reply message
 * @tests SupportModal.test.tsx and frontend/apps/web/e2e/sh-f07.spec.ts
 */

import { useState } from 'react';
import { useActiveOrganizationId } from '../../../app/providers/ActiveRoleProvider';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { ModalDocument } from '../../../shared/components/Modal';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { hasHttpStatus } from '../../../shared/lib/api-error';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useCreateSupportCase, useCreateSupportCaseMessage } from '../api/support-cases.mutations';
import {
  type OrganizationSupportCaseSummary,
  type SupportCaseStatus,
  type SupportCaseSummary,
  useMySupportCases,
  useOrganizationSupportCases,
  useSupportCase,
} from '../api/support-cases.queries';

function newIdempotencyKey(prefix: string): string {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
}

const STATUS_LABEL: Record<SupportCaseStatus, string> = {
  open: '待處理',
  in_progress: '處理中',
  needs_info: '待補充',
  resolved: '已結案',
};

function StatusBadge({ status }: { status: SupportCaseStatus }) {
  const t = useTranslator();
  return (
    <span className={`support-status support-status--${status}`}>{t(STATUS_LABEL[status])}</span>
  );
}

type View =
  | { kind: 'list' }
  | { kind: 'form' }
  | { kind: 'detail'; caseId: string }
  | { kind: 'org-list' };

function NewCaseForm({ onCreated }: { onCreated: (caseId: string) => void }) {
  const t = useTranslator();
  const mutation = useCreateSupportCase();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey('sh-f07-create'));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    try {
      const created = await mutation.mutateAsync({
        subject: subject.trim(),
        message: message.trim(),
        idempotencyKey,
      });
      onCreated(created.id);
    } catch {
      setIdempotencyKey(newIdempotencyKey('sh-f07-create'));
    }
  };

  return (
    <form onSubmit={submit}>
      <label className="support-field">
        <span>{t('主旨')}</span>
        <input
          disabled={mutation.isPending}
          maxLength={200}
          onChange={(event) => setSubject(event.target.value)}
          placeholder={t('簡短描述你的問題')}
          value={subject}
        />
      </label>
      <label className="support-field">
        <span>{t('詳細說明')}</span>
        <textarea
          disabled={mutation.isPending}
          maxLength={4000}
          onChange={(event) => setMessage(event.target.value)}
          placeholder={t('請描述你遇到的狀況，我們會盡快回覆')}
          rows={5}
          value={message}
        />
      </label>
      {mutation.isError ? (
        <p className="support-error" role="alert">
          {t('送出失敗，請稍後再試。')}
        </p>
      ) : null}
      <button
        className="bt pri support-submit"
        disabled={mutation.isPending || !subject.trim() || !message.trim()}
        type="submit"
      >
        {mutation.isPending ? t('送出中…') : t('送出')}
      </button>
    </form>
  );
}

function CaseListRow({
  item,
  onSelect,
  reporterLabel,
}: {
  item: SupportCaseSummary | OrganizationSupportCaseSummary;
  onSelect: () => void;
  reporterLabel?: string;
}) {
  const t = useTranslator();
  return (
    <button className="nrow support-case-row" type="button" onClick={onSelect}>
      <div className="c3">
        <b>{item.subject}</b>
        <p>{item.latestMessagePreview || t('尚無訊息')}</p>
        {reporterLabel ? <em>{reporterLabel}</em> : null}
      </div>
      <StatusBadge status={item.status} />
    </button>
  );
}

function CaseList({
  items,
  isLoading,
  onSelect,
  emptyDescription,
  showReporter,
}: {
  items: readonly (SupportCaseSummary | OrganizationSupportCaseSummary)[] | undefined;
  isLoading: boolean;
  onSelect: (caseId: string) => void;
  emptyDescription: string;
  showReporter?: boolean;
}) {
  const t = useTranslator();
  if (isLoading) return <VispoLoading label={t('正在載入')} />;
  if (!items || items.length === 0) {
    return <p className="support-empty">{t(emptyDescription)}</p>;
  }
  return (
    <div className="inbx support-case-list">
      {items.map((item) => (
        <CaseListRow
          item={item}
          key={item.id}
          onSelect={() => onSelect(item.id)}
          reporterLabel={
            showReporter && 'reporterDisplayName' in item ? item.reporterDisplayName : undefined
          }
        />
      ))}
    </div>
  );
}

function CaseDetail({ caseId, onBack }: { caseId: string; onBack: () => void }) {
  const t = useTranslator();
  const { session } = useWorkspaceSession();
  const caseQuery = useSupportCase(caseId);
  const replyMutation = useCreateSupportCaseMessage(caseId);
  const [draft, setDraft] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey('sh-f07-reply'));

  if (caseQuery.isLoading) return <VispoLoading label={t('正在載入')} />;
  if (caseQuery.isError || !caseQuery.data) {
    return (
      <div className="support-error" role="alert">
        <p>{t('這個案件目前無法載入。')}</p>
        <button className="bt sm" type="button" onClick={() => caseQuery.refetch()}>
          {t('重試')}
        </button>
      </div>
    );
  }
  const supportCase = caseQuery.data;
  // Reply is reporter-only (v1, CONTRACT APPROVED R3) -- an org admin viewing this case from the
  // 組織案件 list must never be offered the reply box, even on a non-resolved case; the backend
  // would correctly reject that write, but the UI must not offer it in the first place (round-1
  // finding 1).
  const isReporter = session?.userId === supportCase.reporterUserId;
  const canReply = isReporter && supportCase.status !== 'resolved';

  const submitReply = async (event: React.FormEvent) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;
    try {
      await replyMutation.mutateAsync({ body, idempotencyKey });
      setDraft('');
      setIdempotencyKey(newIdempotencyKey('sh-f07-reply'));
    } catch {
      // The drafted message stays in the textarea -- never silently discarded on error.
    }
  };

  return (
    <div className="support-case-detail">
      <div className="phd2">
        <button className="pi" type="button" onClick={onBack}>
          {t('返回列表')}
        </button>
      </div>
      <div className="support-case-detail__header">
        <b>{supportCase.subject}</b>
        <StatusBadge status={supportCase.status} />
      </div>
      <div className="inbx support-case-thread">
        {supportCase.messages.map((entry) => (
          <div className="support-message" key={`${entry.authorId}:${entry.createdAt}`}>
            <p>{entry.body}</p>
            <em>{new Date(entry.createdAt).toLocaleString()}</em>
          </div>
        ))}
      </div>
      {canReply ? (
        <form className="support-reply" onSubmit={submitReply}>
          <textarea
            disabled={replyMutation.isPending}
            maxLength={4000}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('補充說明')}
            rows={3}
            value={draft}
          />
          {replyMutation.isError ? (
            <p className="support-error" role="alert">
              {t('送出失敗，請稍後再試。')}
            </p>
          ) : null}
          <button
            className="bt pri support-submit"
            disabled={replyMutation.isPending || !draft.trim()}
            type="submit"
          >
            {replyMutation.isPending ? t('送出中…') : t('送出')}
          </button>
        </form>
      ) : (
        <p className="support-resolved-note">
          {isReporter ? t('此案件已結案，如需協助請開新案件。') : t('只有回報者可以回覆這個案件。')}
        </p>
      )}
    </div>
  );
}

function OrganizationCaseList({ onSelect }: { onSelect: (caseId: string) => void }) {
  const t = useTranslator();
  const organizationId = useActiveOrganizationId();
  const query = useOrganizationSupportCases(organizationId);
  if (query.isLoading) return <VispoLoading label={t('正在載入')} />;
  if (query.isError) {
    if (hasHttpStatus(query.error, 403)) {
      return <p className="support-empty">{t('你沒有查看組織案件的權限。')}</p>;
    }
    return (
      <div className="support-error" role="alert">
        <p>{t('組織案件暫時無法載入。')}</p>
        <button className="bt sm" type="button" onClick={() => query.refetch()}>
          {t('重試')}
        </button>
      </div>
    );
  }
  return (
    <CaseList
      emptyDescription="目前沒有組織案件。"
      isLoading={false}
      items={query.data}
      onSelect={onSelect}
      showReporter
    />
  );
}

export function SupportModal() {
  const t = useTranslator();
  const casesQuery = useMySupportCases();
  const organizationId = useActiveOrganizationId();
  // Fetched unconditionally (not just once the tab is clicked) so the tab's own visibility can be
  // gated on a real success/error split -- an active owner/admin's read succeeds and the tab
  // appears; anyone else's read 403s and the tab never renders at all (round-1 finding 1: gating,
  // not click-then-403).
  const organizationCasesQuery = useOrganizationSupportCases(organizationId);
  const isOrganizationAdmin = organizationCasesQuery.isSuccess;
  const [manualView, setManualView] = useState<View | null>(null);
  const hasActiveCase = casesQuery.data?.some((item) => item.status !== 'resolved') ?? false;
  const view: View =
    manualView ??
    (casesQuery.isLoading ? { kind: 'list' } : hasActiveCase ? { kind: 'list' } : { kind: 'form' });

  const [tab, setTab] = useState<'mine' | 'organization'>('mine');

  let body: React.ReactNode;
  let title = t('聯絡客服');
  if (view.kind === 'detail') {
    title = t('案件詳情');
    body = <CaseDetail caseId={view.caseId} onBack={() => setManualView({ kind: 'list' })} />;
  } else if (view.kind === 'form') {
    title = t('新增客服案件');
    body = <NewCaseForm onCreated={(caseId) => setManualView({ kind: 'detail', caseId })} />;
  } else if (view.kind === 'org-list') {
    body = (
      <OrganizationCaseList onSelect={(caseId) => setManualView({ kind: 'detail', caseId })} />
    );
  } else {
    body = (
      <CaseList
        emptyDescription="目前沒有客服案件。"
        isLoading={casesQuery.isLoading}
        items={casesQuery.data}
        onSelect={(caseId) => setManualView({ kind: 'detail', caseId })}
      />
    );
  }

  // Tabs stay reachable from the form view too -- landing on the form (no active case) must not
  // strand a reporter who actually wants their case history, or an admin who wants 組織案件, with
  // no way to get there. The detail drill-down is the one exception: its own 返回列表 already
  // provides a way back.
  const showTabs = view.kind !== 'detail';

  return (
    <ModalDocument
      className="support-modal"
      dialogLabel={t('聯絡客服')}
      footer={
        view.kind === 'list' || view.kind === 'org-list' ? (
          <button className="bt pri" type="button" onClick={() => setManualView({ kind: 'form' })}>
            {t('開新案件')}
          </button>
        ) : undefined
      }
      maxWidth={520}
      title={title}
    >
      {showTabs ? (
        <div className="ptabs support-tabs">
          <button
            className={tab === 'mine' ? 'on' : ''}
            type="button"
            onClick={() => {
              setTab('mine');
              setManualView({ kind: 'list' });
            }}
          >
            {t('我的案件')}
          </button>
          {isOrganizationAdmin ? (
            <button
              className={tab === 'organization' ? 'on' : ''}
              type="button"
              onClick={() => {
                setTab('organization');
                setManualView({ kind: 'org-list' });
              }}
            >
              {t('組織案件')}
            </button>
          ) : null}
        </div>
      ) : null}
      {body}
    </ModalDocument>
  );
}
